import { Suspense, useDeferredValue } from 'react'
import { createFileRoute, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import { cn } from '@/lib/utils'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { formatCount, formatVital } from './-components/admin/metrics-format'
import { VitalsPending, VitalsSkeleton } from './-components/admin/metrics-pending'
import { DeltaChip, EmptyPanel, MetricsSection, TelemetryNotice } from './-components/admin/metrics-panels'

// What real browsers measured: each Core Web Vital's p75 against Google's bands, overall and per route.

export const Route = createFileRoute('/_app/admin/metrics/vitals')({
  component: RouteComponent,
  pendingComponent: VitalsPending,
})

type Vitals = inferRouterOutputs<AppRouter>['admin']['metricsVitals']
type VitalStat = Vitals['overall']['lcp']
type PageVital = Vitals['pages'][number]['lcp']
type VitalName = keyof Vitals['thresholds']

function useWindowContext() {
  return useMatch({ from: '/_app/admin/metrics/vitals/_window' }).context
}

function RouteComponent() {
  return (
    <div className={SETTINGS_STACK}>
      <MetricsSection copy="vitals">
        <Suspense fallback={<VitalsSkeleton />}>
          <VitalsPanel />
        </Suspense>
      </MetricsSection>
    </div>
  )
}

const VITALS: { key: VitalName; label: string; name: string }[] = [
  { key: 'lcp', label: 'LCP', name: 'Largest contentful paint' },
  { key: 'inp', label: 'INP', name: 'Interaction to next paint' },
  { key: 'cls', label: 'CLS', name: 'Cumulative layout shift' },
  { key: 'fcp', label: 'FCP', name: 'First contentful paint' },
  { key: 'ttfb', label: 'TTFB', name: 'Time to first byte' },
]

const RATING = {
  'good': { label: 'Good', text: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-500/10' },
  'needs-improvement': { label: 'Needs work', text: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-500/15' },
  'poor': { label: 'Poor', text: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/15' },
}

function VitalsPanel() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsVitalsQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsVitalsQueryOptions
  if (data.pages.length === 0)
    return (
      <div className="flex flex-col gap-3">
        <TelemetryNotice state={data.clickhouse} />
        <EmptyPanel>
          {data.clickhouse === 'ok' ? 'No browser reported its vitals in this window.' : 'No vitals to show.'}
        </EmptyPanel>
      </div>
    )
  return (
    <div className={cn('flex flex-col gap-3 transition-opacity', stale && 'opacity-60')}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {VITALS.map((vital) => (
          <VitalCard
            key={vital.key}
            vital={vital}
            stat={data.overall[vital.key]}
            thresholds={data.thresholds[vital.key]}
          />
        ))}
      </div>
      <div className="scrollbar w-full overflow-x-auto rounded-xl border">
        <Table className="min-w-[760px] table-fixed border-collapse">
          <TableHeader className="bg-muted/50">
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[30%] px-3 py-2">Page</TableHead>
              <TableHead className="text-muted-foreground px-3 py-2 text-right">Views</TableHead>
              {VITALS.map((vital) => (
                <TableHead key={vital.key} className="text-muted-foreground px-3 py-2 text-right" title={vital.name}>
                  {vital.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.pages.map((page) => (
              <TableRow key={page.page}>
                <TableCell className="truncate px-3 py-2 font-mono text-xs" title={page.page}>
                  {page.page}
                </TableCell>
                <TableCell className="px-3 py-2 text-right text-xs tabular-nums">{formatCount(page.samples)}</TableCell>
                {VITALS.map((vital) => (
                  <TableCell key={vital.key} className="px-3 py-2 text-right">
                    <VitalValue name={vital.key} stat={page[vital.key]} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function VitalValue(props: { name: VitalName; stat: PageVital }) {
  const rating = props.stat.rating ? RATING[props.stat.rating] : null
  return (
    <span
      className={cn(
        'rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums',
        rating?.bg,
        rating?.text ?? 'text-muted-foreground',
      )}
      title={props.stat.samples ? `${formatCount(props.stat.samples)} samples` : 'no samples'}
    >
      {formatVital(props.name, props.stat.p75)}
    </span>
  )
}

function VitalCard(props: { vital: (typeof VITALS)[number]; stat: VitalStat; thresholds: readonly [number, number] }) {
  const s = props.stat
  const rating = s.rating ? RATING[s.rating] : null
  const middle = Math.max(0, 100 - s.good - s.poor)
  return (
    <div
      className="bg-card flex flex-col gap-2 rounded-lg border p-3"
      title={`${props.vital.name}: ${formatCount(s.samples)} samples`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium">{props.vital.label}</span>
        {rating && <span className={cn('text-[10px] font-medium', rating.text)}>{rating.label}</span>}
      </div>
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className={cn('text-2xl font-semibold tracking-tight tabular-nums', rating?.text)}>
          {formatVital(props.vital.key, s.p75)}
        </span>
        <DeltaChip deltaPct={s.p75DeltaPct} better="down" />
      </span>
      <div className="flex h-1.5 overflow-hidden rounded-full" aria-label={`${s.good}% good, ${s.poor}% poor`}>
        <span className="bg-emerald-500" style={{ width: `${s.good}%` }} />
        <span className="bg-amber-400" style={{ width: `${middle}%` }} />
        <span className="bg-red-500" style={{ width: `${s.poor}%` }} />
      </div>
      <span className="text-muted-foreground text-[10px] tabular-nums">
        good ≤ {formatVital(props.vital.key, props.thresholds[0])} · poor &gt;{' '}
        {formatVital(props.vital.key, props.thresholds[1])}
      </span>
    </div>
  )
}
